import './index.scss';
import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { format } from 'date-fns';
import { TEMP_COLUMNS, TriOpDateColumns } from './columns/column-list';
import PanelDetails from './tab-content/panel-details';
import { COMPONENT_NAME } from './tab-content/panel-details/shared';
import TimelineTabContent from './tab-content/timeline-tab';
import ChipList from '@components-clean/core/chip-list';
import CoreSplitter from '@components-clean/core/core-splitter';
import ErrorComponent from '@components-clean/core/error';
import Grid from '@components-clean/core/grid-fe';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import TabStripLowDom from '@components-clean/core/tabstrip-low-dom';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import {
    getMainChipsMap,
    getSubChipsListGraphColumn,
    getSubChipsListGraphLine,
    getSubChipsListModelNonTrend,
    getSubChipsListModelTrend,
} from '@pages-clean/admin/study/manage-panels/chips';
import EventRatesOTGrid from '@pages-clean/admin/study/manage-panels/columns/event-rates-OT-grid';
import ProportionsOTGrid from '@pages-clean/admin/study/manage-panels/columns/proportions-OT-grid';
import { PanelDetailsContext } from '@pages-clean/admin/study/manage-panels/context';
import {
    MainChipHeaderValues,
    SubChipHeaderValuesModelNonTrend,
    SubChipHeaderValuesModelTrend,
} from '@pages-clean/admin/study/manage-panels/enums';
import { IChipData, ISubChipData } from '@pages-clean/admin/study/manage-panels/interfaces';
import {
    Context,
    IListItems,
} from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/timeline/shared/context';
import { Options } from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/tri-op-date/interfaces';
import { getTimelinePanelListItems, ManagePanelSplitterPanes } from '@pages-clean/admin/study/manage-panels/utils';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { getPanels_OpraQueries_panels_Panel, getPanels } from 'queries/generated/central-monitoring.generated';
import {
    AuditApplicationArea,
    AuditDataType,
    AuditServiceType,
    PanelModelType,
    PanelType,
    RateGroupType,
} from 'types/graphql-global-types';
import { UserContext } from '@contexts/user-context';
import { useLocalState } from '@hooks/index';
import { SplitterOrientation } from '@utils/constants';
import { GET_PANELS } from '@queries/central-monitoring';
import {
    PanelFields,
    PanelFields_Panel_modelParams_PanelModelParams as PanelFields_modelParams,
} from '@queries/generated/fragments.generated';

interface IProps {
    orgId: string;
    studyRef: string;
}

interface IPropsWithChips {
    orgId: string;
    studyRef: string;
    title: string;
    type: PanelType;
    chips?: IChipData[];
    studyNumber: string;
    canEdit: boolean;
}

export type GenericPanel = getPanels_OpraQueries_panels_Panel;
export type GridPanel = PanelFields & {
    panelTypeLabel?: string | null;
    model?: string | null;
};

const FilteredPanelsContent = ({ orgId, studyRef, title, type, chips, studyNumber, canEdit }: IPropsWithChips) => {
    const {
        data,
        error,
        refetch,
        loading: isLoading,
    } = useQuery<getPanels>(GET_PANELS, {
        variables: {
            organisationId: orgId,
            studyId: studyRef,
        },
    });

    const [mainSelection, setMainSelection] = React.useState<IChipData | undefined>(chips![0]);
    const [subSelection, setSubSelection] = React.useState<ISubChipData | undefined>();
    const [isCreating, setIsCreating] = React.useState<boolean>(false);
    const [justCreatedPanelRef, setJustCreatedPanelRef] = React.useState<string | undefined>(undefined);
    const [selectedPanel, setSelectedPanel] = React.useState<GenericPanel | undefined>(undefined);
    const [panelTitle, setPanelTitle] = React.useState<string>(title);
    const [panels, setPanels] = React.useState<(GenericPanel | null)[]>();
    const [panelColumns, setPanelColumns] = React.useState<GridColumnProps[]>(TEMP_COLUMNS);
    const { state } = useLocalState();
    const [exportFileName, setExportFileName] = React.useState<string>();
    const { t: tOptions } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.triOpDate.options' });
    const {
        SiteAverageOptions,
        DataInclusionPeriodOptions,
        DataPopulationOptions,
        DataInclusionUnits,
        ThresholdCalculationOptions,
        StableTypeOptions,
    } = Options(tOptions);

    const { t } = useTranslation(['admin'], { keyPrefix: 'managePanels' });
    const mainChipsList = getMainChipsMap(t);

    const proportionsOTGrid = new ProportionsOTGrid(state?.cm2Fields, state?.cm2DataSources);
    const eventRatesOTGrid = new EventRatesOTGrid(state?.cm2Fields, state?.cm2DataSources);

    useEffect(() => {
        if (!data?.panels || !justCreatedPanelRef) return;
        const newlyCreatedPanel = data.panels.find((p) => p && p.id === justCreatedPanelRef) ?? undefined;
        setSelectedPanel(newlyCreatedPanel);
        setJustCreatedPanelRef(undefined);
    }, [data?.panels]);

    const { componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const filteredPanels = (subChipSelected: ISubChipData) => {
        switch (true) {
            case type === 'MODEL' &&
                mainSelection?.mainChip.value === mainChipsList[MainChipHeaderValues.ModelNonTrend].value &&
                subChipSelected.value === SubChipHeaderValuesModelNonTrend.TRIOpsDate:
                const triOpDatePanels = data?.panels?.filter((panel) => panel?.panelType === PanelType.TRI_OP_DATE);
                setPanelTitle(t('grid.tri-ops-date.title'));
                setExportFileName(`TRI Ops-Date Panels_${studyNumber}_${format(new Date(), 'dd-MMM-yyyy')}`);
                const triOpDatePanelsFormatted = triOpDatePanels
                    ?.map((panel) => ({
                        ...panel,
                        sortField: state?.cm2Fields?.find(
                            (field) => field?.key === panel?.triOpDateParameters?.sortField,
                        )?.name,
                        yAxisData: state?.cm2Fields?.find(
                            (field) => field?.key === panel?.triOpDateParameters?.yAxisDataField,
                        )?.name,

                        updatedByFullName: `${panel?.updatedBy?.firstName} ${panel?.updatedBy?.lastName}`,
                        createdByFullName: `${panel?.createdBy?.firstName} ${panel?.createdBy?.lastName}`,
                        active: panel?.active || false,
                        name: panel?.name || '',
                        siteAverage: SiteAverageOptions.find(
                            (siteAverage) => siteAverage.id === panel?.triOpDateParameters?.siteAverage,
                        )?.name,
                        dataPopulation: DataPopulationOptions.find(
                            (dp) => dp.id === panel?.triOpDateParameters?.dataPopulation,
                        )?.name,
                        thresholdCalculation: ThresholdCalculationOptions.find(
                            (th) => th.id === panel?.triOpDateParameters?.thresholdCalculation,
                        )?.name,
                        dataInclusionPeriod: DataInclusionPeriodOptions.find(
                            (dp) => dp.id === panel?.triOpDateParameters?.dataInclusionPeriod,
                        )?.name,
                        stableVariableType: StableTypeOptions.find(
                            (st) => st.id === panel?.triOpDateParameters?.stableVariableType,
                        )?.name,
                        dataInclusionUnit: DataInclusionUnits.find(
                            (di) => di.id === panel?.triOpDateParameters?.dataInclusionUnit,
                        )?.name,
                        rowData: state?.cm2DataSources?.find(
                            (field) => field?.id === panel?.triOpDateParameters?.rowData,
                        )?.name,
                    }))
                    .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));

                setPanelColumns(TriOpDateColumns(t, tOptions));
                // @ts-ignore
                setPanels(triOpDatePanelsFormatted);
                break;
            case type === 'MODEL' &&
                mainSelection?.mainChip.value === mainChipsList[MainChipHeaderValues.ModelTrend].value &&
                subChipSelected.value === SubChipHeaderValuesModelTrend.ProportionsOT:
                setPanelTitle(proportionsOTGrid.GridTitle());
                setExportFileName(
                    `${proportionsOTGrid.ExportName()} Panels_${studyNumber}_${format(new Date(), 'dd-MMM-yyyy')}`,
                );
                setPanelColumns(proportionsOTGrid.Columns());
                setPanels(proportionsOTGrid.PanelData(data?.panels));
                break;
            case type === 'MODEL' &&
                mainSelection?.mainChip.value === mainChipsList[MainChipHeaderValues.ModelTrend].value &&
                subChipSelected.value === SubChipHeaderValuesModelTrend.EventRatesOT:
                setPanelTitle(eventRatesOTGrid.GridTitle());
                setExportFileName(
                    `${eventRatesOTGrid.ExportName()} Panels_${studyNumber}_${format(new Date(), 'dd-MMM-yyyy')}`,
                );
                setPanelColumns(eventRatesOTGrid.Columns());
                setPanels(eventRatesOTGrid.PanelData(data?.panels));
                break;
            default:
                setPanels(data?.panels || []);
                setPanelColumns(TEMP_COLUMNS);
                setPanelTitle(''); // Unsupported panel so no need for a title
                setExportFileName(`Model_Panels_${studyNumber}_${format(new Date(), 'dd-MMM-yyyy')}`);
        }
    };

    useEffect(() => {
        setMainSelection(chips![0]);
        setSubSelection(undefined);
    }, [type]);

    useEffect(() => {
        if (!mainSelection?.subChips) return;
        const selectedSubChipIndex = mainSelection.subChips.findIndex((subChip) =>
            subSelection ? subSelection?.value === subChip.value : subChip.selected,
        );
        setSubSelection(mainSelection.subChips[selectedSubChipIndex]);
        filteredPanels(mainSelection.subChips[selectedSubChipIndex]);
    }, [mainSelection, data?.panels]);

    let content;

    const showGrid = (): boolean => {
        return (
            (mainSelection?.mainChip.value === mainChipsList[MainChipHeaderValues.ModelNonTrend].value &&
                subSelection?.value === SubChipHeaderValuesModelNonTrend.TRIOpsDate) ||
            (mainSelection?.mainChip.value === mainChipsList[MainChipHeaderValues.ModelTrend].value &&
                (subSelection?.value === SubChipHeaderValuesModelTrend.ProportionsOT ||
                    subSelection?.value === SubChipHeaderValuesModelTrend.EventRatesOT))
        );
    };

    if (error) {
        content = <InfoField type="Error" message="Failed to load panels." />;
    } else if (isLoading) {
        content = <Loading />;
    } else {
        content = (
            <div className={cn('content', { 'with-chiplist': !!chips })}>
                {chips && mainSelection?.mainChip.value !== null && (
                    <div className="chip-area">
                        <ChipList
                            // We use a compound key here to prevent a bug with the focus state of the chiplist causing
                            // some rerenders to steal focus from input elements
                            key={String(componentHasChanges(COMPONENT_NAME))}
                            data-testid="main-chips"
                            data={chips.map((chip) => chip.mainChip)}
                            value={mainSelection!.mainChip.value}
                            onChange={async (e) => {
                                if (componentHasChanges(COMPONENT_NAME)) {
                                    const confirmed = await confirmDiscardChanges();
                                    if (!confirmed) return;
                                }

                                if (e.value == null) {
                                    return;
                                }
                                setMainSelection(chips.find((c) => c.mainChip.value === e.value)!);
                                setSubSelection(undefined);
                                setIsCreating(false);
                            }}
                        />
                        {mainSelection?.subChips && (
                            <>
                                <div className="splitter" />

                                <ChipList
                                    // We use a compound key here to prevent a bug with the focus state of the chiplist causing
                                    // some rerenders to steal focus from input elements
                                    key={String(componentHasChanges(COMPONENT_NAME))}
                                    data-testid="sub-chips"
                                    data={mainSelection?.subChips}
                                    secondary
                                    onChange={async (e) => {
                                        if (componentHasChanges(COMPONENT_NAME)) {
                                            const confirmed = await confirmDiscardChanges();
                                            if (!confirmed) return;
                                        }

                                        if (e.value == null) {
                                            return;
                                        }

                                        const subChip = mainSelection?.subChips!.find((x) => x.value === e.value);

                                        if (subChip) {
                                            setSubSelection(subChip);
                                            filteredPanels(subChip);
                                            setIsCreating(false);
                                        }
                                    }}
                                    value={subSelection?.value}
                                />
                            </>
                        )}
                    </div>
                )}

                {showGrid() ? (
                    <CoreSplitter
                        className="!k-h-full"
                        alignmentValues={ManagePanelSplitterPanes}
                        orientation={SplitterOrientation.vertical}
                        data-testid="manage-panels-content"
                    >
                        <Grid
                            title={panelTitle}
                            testDataId="panels-grid"
                            maxHeight="auto"
                            selectable={{
                                enabled: true,
                                mode: 'single',
                            }}
                            key={`${mainSelection?.mainChip.value}-${subSelection?.value}`}
                            dataItemKey="id"
                            scrollable="scrollable"
                            exportFilename={exportFileName}
                            columns={panelColumns}
                            data={panels}
                            canEdit={
                                canEdit &&
                                (type === PanelType.TIMELINE ||
                                    subSelection?.value === SubChipHeaderValuesModelNonTrend.TRIOpsDate ||
                                    subSelection?.value === SubChipHeaderValuesModelTrend.ProportionsOT ||
                                    subSelection?.value === SubChipHeaderValuesModelTrend.EventRatesOT)
                            }
                            defaultSelectedItem={selectedPanel}
                            preSelectionChange={async () => {
                                if (componentHasChanges(COMPONENT_NAME)) {
                                    const confirmed = await confirmDiscardChanges();
                                    if (!confirmed) return false;
                                }

                                return true;
                            }}
                            onSelectionChange={async ({ startDataItem }) => {
                                const panel = startDataItem as PanelFields;
                                setSelectedPanel(panel);
                            }}
                            addCallback={() => {
                                setSelectedPanel(undefined);
                                setIsCreating(true);
                            }}
                            service={AuditServiceType.CENTRAL_MONITORING_2}
                            area={AuditApplicationArea.PANEL}
                            dataType={AuditDataType.EXCEL_EXPORT}
                            auditDescriptionPrefix="Study Admin - Manage Panels"
                        />

                        <PanelDetails
                            canEdit={canEdit}
                            createData={
                                isCreating
                                    ? {
                                          type: subSelection?.text,
                                          onCreate: async (newPanelRef: string) => {
                                              setIsCreating(false);
                                              setJustCreatedPanelRef(newPanelRef);
                                              await refetch();
                                          },
                                          onCancel: () => {
                                              setIsCreating(false);
                                          },
                                      }
                                    : undefined
                            }
                            panel={
                                selectedPanel && {
                                    id: selectedPanel.id ?? '',
                                    type: selectedPanel.panelType as PanelType,
                                    modelType:
                                        selectedPanel.panelType === 'MODEL' && selectedPanel.modelParams?.type
                                            ? selectedPanel?.modelParams?.type
                                            : undefined,
                                }
                            }
                        />
                    </CoreSplitter>
                ) : (
                    <div className="k-h-full k-d-flex-col k-justify-content-center">
                        <InfoField type="Info" message={t('panelDetails.empty.underConstruction')} />
                    </div>
                )}
            </div>
        );
    }

    return <div className="model-panels">{content}</div>;
};

const AllPanelsContent = ({ orgId, studyRef }: IProps) => {
    const { state } = useLocalState();

    const {
        data,
        error,
        loading: isLoading,
    } = useQuery(GET_PANELS, {
        variables: {
            organisationId: orgId,
            studyId: studyRef,
        },
        fetchPolicy: 'no-cache',
    });

    const listItems: IListItems = useMemo(
        () => getTimelinePanelListItems(state),
        [state!.cm2DataSources, state!.cm2Fields],
    );

    const [panels, setPanels] = React.useState<GridPanel[]>([]);
    const [selectedPanel, setSelectedPanel] = React.useState<PanelFields | undefined>(undefined);
    const { t } = useTranslation();
    const { componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    useEffect(() => {
        // This method is copied and adapted from the legacy code. This needs to be refactored later.
        const getPanelModel = (modelParams: PanelFields_modelParams | null) => {
            switch (modelParams?.type) {
                case PanelModelType.RATE_GROUP:
                    if (modelParams?.rateGroupType === RateGroupType.NEGATIVE_BINOMIAL) {
                        return 'NEGATIVE_BINOMIAL';
                    }
                    return 'RATE_GROUP';
                case PanelModelType.RATE_GROUP_TREND:
                    return 'RATE_GROUP_TREND';
                default:
                    return modelParams?.type;
            }
        };

        if (data && data.panels) {
            const items = data.panels.map((panel: PanelFields): GridPanel => {
                const model = getPanelModel(panel.modelParams);

                let panelTypeLabel;

                if (
                    panel.panelType === PanelType.TIMELINE ||
                    panel.panelType === PanelType.TRI_OP_DATE ||
                    panel.panelType === PanelType.PROPORTIONS_OVER_TIME ||
                    panel.panelType === PanelType.EVENT_RATES_OVER_TIME
                ) {
                    panelTypeLabel = t(`cm:${panel.panelType}`);
                } else if (panel.panelType === PanelType.MODEL) {
                    panelTypeLabel = t('cm:Model');
                } else {
                    panelTypeLabel = t(`cm:${panel.graph?.type}`);
                }

                return {
                    ...panel,
                    panelTypeLabel,
                    model: panel.panelType === PanelType.MODEL ? t(`cm:${model}`) : 'N/A',
                };
            });
            const newData = restructuredUpdatedCreatedBy(items) as GridPanel[];

            // Need to sort by Active (true first), then Plot Title ascending
            const sortedNewData = newData.sort((a, b) => {
                // (true → 1, false/null → 0)
                const aActive = a.active ? 1 : 0;
                const bActive = b.active ? 1 : 0;

                // Primary sort: active descending (true first)
                if (bActive !== aActive) {
                    return bActive - aActive;
                }

                const aTitle = a.plotTitle ?? '';
                const bTitle = b.plotTitle ?? '';

                // Secondary sort: alphabetical ascending
                return aTitle.localeCompare(bTitle);
            });

            setPanels(sortedNewData);
        }
    }, [data]);

    let content;

    if (error) {
        content = <InfoField type="Error" message="Failed to load panels." />;
    } else if (isLoading) {
        content = <Loading />;
    } else {
        content = (
            <div className="content">
                <CoreSplitter
                    className="!k-h-full"
                    alignmentValues={ManagePanelSplitterPanes}
                    orientation={SplitterOrientation.vertical}
                    data-testid="manage-panels-content"
                >
                    <Grid
                        title="All Panels"
                        testDataId="all-panels-grid"
                        maxHeight="auto"
                        selectable={{
                            enabled: true,
                            mode: 'single',
                        }}
                        dataItemKey="id"
                        scrollable="scrollable"
                        exportFilename={`${state?.selectedStudy?.studyNumber}_All Panels_${new Date()
                            .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            .replaceAll(' ', '-')}`}
                        columns={TEMP_COLUMNS}
                        data={panels}
                        defaultSelectedItem={selectedPanel}
                        preSelectionChange={async () => {
                            if (componentHasChanges(COMPONENT_NAME)) {
                                const confirmed = await confirmDiscardChanges();
                                if (!confirmed) return false;
                            }

                            return true;
                        }}
                        onSelectionChange={async ({ startDataItem }) => {
                            const panel = startDataItem as PanelFields;
                            setSelectedPanel(panel);
                        }}
                        service={AuditServiceType.CENTRAL_MONITORING_2}
                        area={AuditApplicationArea.PANEL}
                        dataType={AuditDataType.EXCEL_EXPORT}
                        auditDescriptionPrefix="Study Admin - Manage Panels"
                        showAddIcon={false}
                    />
                    <PanelDetails
                        panel={
                            selectedPanel && {
                                id: selectedPanel.id ?? '',
                                type: selectedPanel.panelType as PanelType,
                                modelType:
                                    selectedPanel.panelType === PanelType.MODEL && selectedPanel.modelParams?.type
                                        ? selectedPanel?.modelParams?.type
                                        : undefined,
                            }
                        }
                    />
                </CoreSplitter>
            </div>
        );
    }

    const context = useMemo(
        () => ({
            organisationId: orgId,
            studyId: studyRef,
            listItems,
            // Not changing filters on the grid, so set to false
            hasFilters: false,
        }),
        [orgId, studyRef, listItems],
    );

    return (
        <Context.Provider value={context}>
            <div className="all-panels">{content}</div>
        </Context.Provider>
    );
};

const ManagePanels = () => {
    const { user } = useContext(UserContext);
    const canEdit = user?.canWriteSystemAdminLevel?.(false, false, false, true) ?? false;

    const { t } = useTranslation(['admin'], { keyPrefix: 'managePanels' });
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const { state } = useLocalState();

    if (!user || !state) {
        return <ErrorComponent error={t('unexpectedError')} />;
    }

    const study = state.selectedStudy;

    if (!study) {
        return <span>{t('pleaseSelectStudy')}</span>;
    }

    const mainChipsList = getMainChipsMap(t);

    const modelTabChips = [
        {
            mainChip: mainChipsList[MainChipHeaderValues.ModelNonTrend],
            subChips: getSubChipsListModelNonTrend(t),
        },
        {
            mainChip: mainChipsList[MainChipHeaderValues.ModelTrend],
            subChips: getSubChipsListModelTrend(t),
        },
    ];

    const graphTabChips = [
        {
            mainChip: mainChipsList[MainChipHeaderValues.GraphColumn],
            subChips: getSubChipsListGraphColumn(t),
        },
        {
            mainChip: mainChipsList[MainChipHeaderValues.GraphDonut],
        },
        {
            mainChip: mainChipsList[MainChipHeaderValues.GraphHeatMap],
        },
        {
            mainChip: mainChipsList[MainChipHeaderValues.GraphLine],
            subChips: getSubChipsListGraphLine(t),
        },
    ];

    if (!state?.cm2DataSources) return <Loading />;

    return (
        <div className="manage-panels">
            <TabStripLowDom
                data={[
                    {
                        title: 'All',
                        dataTestId: 'all-panels',
                        content: (
                            <PanelDetailsContext value={{ source: 'All' }}>
                                <AllPanelsContent orgId={user.organisationId as string} studyRef={study.id as string} />
                            </PanelDetailsContext>
                        ),
                    },
                    {
                        title: 'Model',
                        dataTestId: 'model-panels',
                        content: (
                            <PanelDetailsContext value={{ source: 'Filtered' }}>
                                <FilteredPanelsContent
                                    orgId={user.organisationId as string}
                                    studyRef={study.id as string}
                                    studyNumber={study.studyNumber as string}
                                    title="Model Panels"
                                    type={PanelType.MODEL}
                                    canEdit={canEdit}
                                    chips={modelTabChips}
                                />
                            </PanelDetailsContext>
                        ),
                    },
                    {
                        title: 'Graph',
                        dataTestId: 'graph-panels',
                        content: (
                            <PanelDetailsContext value={{ source: 'Filtered' }}>
                                <FilteredPanelsContent
                                    orgId={user.organisationId as string}
                                    studyRef={study.id as string}
                                    title="Graph Panels"
                                    type={PanelType.GRAPH}
                                    chips={graphTabChips}
                                    canEdit={canEdit}
                                    studyNumber={study.studyNumber as string}
                                />
                            </PanelDetailsContext>
                        ),
                    },
                    {
                        title: 'Timeline',
                        dataTestId: 'timeline-panels',
                        content: (
                            <PanelDetailsContext value={{ source: 'Filtered' }}>
                                <TimelineTabContent
                                    organisationId={user.organisationId as string}
                                    studyId={study.id as string}
                                />
                            </PanelDetailsContext>
                        ),
                    },
                ]}
                canChangeTab={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) {
                            return false;
                        }
                        setComponentHasChanges(COMPONENT_NAME, false);
                    }
                    return true;
                }}
            />
        </div>
    );
};

export default ManagePanels;
